import React, { FC, useCallback, useId, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import {
  buildInviteUrl,
  PUBLIC_WEB_ORIGIN,
  shortTableCode,
} from '../lib/invite';

/** Native builds run on capacitor://localhost, so share the public web URL. */
export const getShareOrigin = () => {
  const configured = process.env.REACT_APP_PUBLIC_WEB_ORIGIN;
  if (configured) return configured;
  if (Capacitor.isNativePlatform()) return PUBLIC_WEB_ORIGIN;
  return window.location.origin;
};

/** Clipboard API first, then the legacy selection fallback. */
export const copyText = async (
  text: string,
  fallbackInput?: HTMLInputElement | null
): Promise<boolean> => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the legacy path
  }
  try {
    if (fallbackInput) {
      fallbackInput.focus();
      fallbackInput.select();
      fallbackInput.setSelectionRange(0, text.length);
    }
    return document.execCommand('copy');
  } catch {
    return false;
  }
};

type ShareStatus = 'idle' | 'copied' | 'shared' | 'manual';

const STATUS_COPY: Record<ShareStatus, string> = {
  idle: '',
  copied: 'Link copied. Paste it into your group chat.',
  shared: 'Invite sent.',
  manual:
    "Couldn't copy automatically. The link is selected, so copy it from the box.",
};

export interface SharePanelProps {
  roomID: string;
  seats: number;
  hostName?: string;
  /** "ivory" when rendered on an st-card, "felt" inside a dark panel. */
  surface?: 'ivory' | 'felt';
}

export const SharePanel: FC<SharePanelProps> = ({
  roomID,
  seats,
  hostName,
  surface = 'ivory',
}) => {
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<ShareStatus>('idle');
  const url = buildInviteUrl(getShareOrigin(), roomID);
  const canNativeShare =
    typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const onCopy = useCallback(async () => {
    const ok = await copyText(url, inputRef.current);
    setStatus(ok ? 'copied' : 'manual');
    if (!ok) inputRef.current?.select();
  }, [url]);

  const onShare = useCallback(async () => {
    try {
      await navigator.share({
        title: 'Join my Bhabi table',
        text: `${hostName ? `${hostName} saved you` : 'Pull up'} a seat at a ${seats}-player Bhabi table. Tap to sit down:`,
        url,
      });
      setStatus('shared');
    } catch (err) {
      // user dismissed the sheet: say nothing; otherwise fall back to copy
      if ((err as DOMException)?.name !== 'AbortError') {
        await onCopy();
      }
    }
  }, [hostName, onCopy, seats, url]);

  const onIvory = surface === 'ivory';

  return (
    <section className="st-stack" aria-labelledby={`${id}-title`}>
      <div className="st-row" style={{ justifyContent: 'space-between' }}>
        <h2 id={`${id}-title`} className="st-h3">
          Invite your table
        </h2>
        <span className="st-small st-muted">
          Table code{' '}
          <span className="st-code" style={{ fontSize: '1rem' }}>
            {shortTableCode(roomID)}
          </span>
        </span>
      </div>
      <div className="st-share-link">
        <label htmlFor={`${id}-link`} className="st-sr-only">
          Invite link
        </label>
        <input
          ref={inputRef}
          id={`${id}-link`}
          className={`st-input ${onIvory ? '' : 'st-input--dark'}`}
          readOnly
          value={url}
          onFocus={e => e.currentTarget.select()}
        />
        <button
          type="button"
          className={`st-btn ${onIvory ? 'st-btn--on-ivory' : 'st-btn--secondary'}`}
          onClick={onCopy}
        >
          Copy
        </button>
      </div>
      {canNativeShare && (
        <button
          type="button"
          className="st-btn st-btn--primary st-btn--block"
          onClick={onShare}
        >
          Share invite link
        </button>
      )}
      <p className="st-status st-small" role="status" aria-live="polite">
        {STATUS_COPY[status]}
      </p>
    </section>
  );
};
